import { create } from 'zustand';
import { api, authApi, refreshSession, setSessionLostHandler, type SelfUser } from '../lib/api';
import { lobbySocket } from '../lib/lobbySocket';
import { useSettings } from './settings';

interface SessionState {
  status: 'loading' | 'anon' | 'authed';
  user: SelfUser | null;
  bootstrap: () => Promise<void>;
  signedIn: (u: SelfUser) => void;
  reloadMe: () => Promise<void>;
  updateMe: (patch: Partial<Pick<SelfUser, 'displayName' | 'country' | 'locale' | 'skin' | 'theme' | 'deathEffect' | 'avatar'>>) => Promise<void>;
  logout: () => Promise<void>;
}

export const useSession = create<SessionState>((set, get) => ({
  status: 'loading',
  user: null,

  async bootstrap() {
    setSessionLostHandler(() => {
      lobbySocket.stop();
      set({ status: 'anon', user: null });
    });
    const r = await refreshSession();
    if (r) get().signedIn(r.user);
    else set({ status: 'anon' });
  },

  signedIn(user) {
    set({ status: 'authed', user });
    useSettings.getState().set({ skin: user.skin, theme: user.theme });
    lobbySocket.start();
  },

  async reloadMe() {
    const user = await api<SelfUser>('/api/me');
    set({ user });
  },

  async updateMe(patch) {
    const user = await api<SelfUser>('/api/me', { method: 'PATCH', body: patch });
    set({ user });
    useSettings.getState().set({ skin: user.skin, theme: user.theme });
  },

  async logout() {
    lobbySocket.stop();
    await authApi.logout();
    set({ status: 'anon', user: null });
  },
}));
